/**
 * Driver geofence rules — one implementation shared by the web driver app.
 *
 * Mirrors the backend (`_geofence_limits` in main.py) and the iOS driver app
 * (`src/utils/geofence.ts`):
 *   effective radius = 150 m + min(reported GPS accuracy, 100 m)
 *   accuracy absent / invalid         -> 150 m (no allowance)
 *   accuracy > 250 m ("weak")         -> 150 m (no allowance), never auto-arrives
 * The backend always re-checks; this only decides what the UI lets through.
 */

import {
  GEOFENCE_RADIUS_M,
  GEOFENCE_MAX_ACCURACY_ALLOWANCE_M,
  GEOFENCE_MAX_USABLE_ACCURACY_M,
} from "../constants/operations";

/** Haversine distance between two lat/lng points in metres. */
export function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6_371_000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** A usable accuracy value in metres, or null when absent/invalid. */
export function cleanAccuracy(accuracy) {
  if (accuracy === null || accuracy === undefined || accuracy === "" || typeof accuracy === "boolean") {
    return null;
  }
  const n = Number(accuracy);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** True when the reading is too coarse to trust for geofencing. */
export function isWeakAccuracy(accuracy) {
  const acc = cleanAccuracy(accuracy);
  return acc !== null && acc > GEOFENCE_MAX_USABLE_ACCURACY_M;
}

/** Effective fence radius (m) for a reading with the given accuracy. */
export function geofenceRadius(accuracy) {
  const acc = cleanAccuracy(accuracy);
  if (acc === null || acc > GEOFENCE_MAX_USABLE_ACCURACY_M) return GEOFENCE_RADIUS_M;
  return GEOFENCE_RADIUS_M + Math.min(acc, GEOFENCE_MAX_ACCURACY_ALLOWANCE_M);
}

/** " (GPS accuracy ±N m)" suffix for driver-facing messages, or "". */
export function accuracyNote(accuracy) {
  const acc = cleanAccuracy(accuracy);
  return acc === null ? "" : ` (GPS accuracy ±${Math.round(acc)} m)`;
}

const num = (v, limit) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
};

/**
 * Stored position of a stop: { lat, lng } or null.
 * `prefix` is "pickup" | "dropoff". Reads the flat pickupLat/pickupLng fields
 * the backend stores, falling back to the nested pickupCoordinates form.
 */
export function stopCoords(task, prefix) {
  const lat = num(task?.[`${prefix}Lat`], 90);
  const lng = num(task?.[`${prefix}Lng`], 180);
  if (lat !== null && lng !== null) return { lat, lng };
  const nested = task?.[`${prefix}Coordinates`];
  const nLat = num(nested?.lat, 90);
  const nLng = num(nested?.lng, 180);
  return nLat !== null && nLng !== null ? { lat: nLat, lng: nLng } : null;
}

/**
 * Is a driver reading inside the fence around `target`?
 * @param {{lat:number,lng:number,accuracy?:number|null}} driver
 * @param {{lat:number,lng:number}} target
 * @returns {{distanceM:number, radiusM:number, isInside:boolean, weak:boolean}}
 */
export function checkGeofence(driver, target) {
  const distanceM = haversineMeters(driver.lat, driver.lng, target.lat, target.lng);
  const radiusM = geofenceRadius(driver.accuracy);
  return {
    distanceM,
    radiusM,
    isInside: distanceM <= radiusM,
    weak: isWeakAccuracy(driver.accuracy),
  };
}
